// 店舗状況ページ（ユーザー向け）- 本名は一切表示せずニックネームのみ使用
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, get } from 'firebase/database';
import { mainDatabase } from '../../firebase';
import shared from '../../styles/shared.module.css';
import styles from '../setting/Settings.module.css';
import localStyles from './StoreStatus.module.css';

// ローカル日付で「今日」を判定（UTC だと日本時間の昨日が含まれるバグを防ぐ）
const getLocalDateString = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getTodayDateString = () => getLocalDateString(new Date());

const isToday = (dateString) => {
  if (!dateString) return false;
  return getLocalDateString(dateString) === getTodayDateString();
};

const formatTime = (dateString) => {
  if (!dateString) return '--:--';
  const d = new Date(dateString);
  return new Intl.DateTimeFormat('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(d);
};

// 訪問回数はローカル日付で集計（1日 = そのタイムゾーンの日付）
const getOrderDateKey = (orderedAt) => {
  if (!orderedAt) return '';
  return getLocalDateString(orderedAt);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
};

const StoreStatusPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('status'); // 'status' | 'ranking'
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState({}); // userId -> { nickname, isAdmin }
  const [loading, setLoading] = useState(true);
  const [rankingDetailUserId, setRankingDetailUserId] = useState(null); // タップしたランキングの userId（詳細表示用）
  const [participantDetailUserId, setParticipantDetailUserId] = useState(null); // クリックした参加者（本日注文ポップアップ用）

  // メインDBから注文一覧を取得
  useEffect(() => {
    const ordersRef = ref(mainDatabase, 'orders');
    const unsubOrders = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      const list = data
        ? Object.entries(data).map(([id, v]) => ({ id, ...v }))
        : [];
      setOrders(list);
    });
    return () => unsubOrders();
  }, []);

  // メインDBからユーザー一覧を取得（isAdmin 判定用。非管理者は users の一覧読取が制限されることがある）
  useEffect(() => {
    const usersRef = ref(mainDatabase, 'users');
    get(usersRef).then((snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setUsers({});
        setLoading(false);
        return;
      }
      const map = {};
      Object.entries(data).forEach(([uid, u]) => {
        const nickname = (u && (u.nickname || '').trim()) ? u.nickname : '';
        map[uid] = { nickname: nickname || 'ゲスト', isAdmin: u?.isAdmin === true };
      });
      setUsers(map);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // 表示名: 注文の userNickname を優先（非管理者でも orders は読めるため）。userName は本名を含むため使用しない
  const displayNameFromOrders = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      if (o.userId && o.userNickname) {
        const nick = (o.userNickname || '').trim();
        if (nick && !map[o.userId]) map[o.userId] = nick;
      }
    });
    return map;
  }, [orders]);

  const displayName = (userId) => {
    // 注文の userNickname を優先（非管理者は users を読めないため）
    const fromOrders = displayNameFromOrders[userId];
    if (fromOrders) return fromOrders;
    const fromUsers = users[userId]?.nickname;
    if (fromUsers) return fromUsers;
    return 'ゲスト';
  };

  // アイコン用イニシャル（ニックネームの頭文字、ゲストは ?）
  const getInitial = (userId) => {
    const name = displayName(userId);
    if (!name || name === 'ゲスト') return '?';
    return name.charAt(0).toUpperCase();
  };

  const todayOrders = useMemo(() => {
    return orders
      .filter((o) => isToday(o.orderedAt))
      .sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt));
  }, [orders]);

  const todayParticipantIds = useMemo(() => {
    const set = new Set(todayOrders.map((o) => o.userId).filter(Boolean));
    return Array.from(set);
  }, [todayOrders]);

  // 訪問回数 = 注文があった「日」の数（1日1回とカウント）。管理者はランキング不参加
  const rankingByUser = useMemo(() => {
    const visitDatesByUser = {}; // userId -> Set<dateKey>
    const ordersByUser = {};     // userId -> orders[]
    orders.forEach((o) => {
      const uid = o.userId || '';
      if (!uid) return;
      if (users[uid]?.isAdmin) return; // 管理者は除外
      const dateKey = getOrderDateKey(o.orderedAt);
      if (!dateKey) return;
      if (!visitDatesByUser[uid]) {
        visitDatesByUser[uid] = new Set();
        ordersByUser[uid] = [];
      }
      visitDatesByUser[uid].add(dateKey);
      ordersByUser[uid].push(o);
    });
    return Object.entries(visitDatesByUser)
      .map(([uid]) => ({
        userId: uid,
        visitCount: visitDatesByUser[uid].size,
        orders: (ordersByUser[uid] || []).sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt))
      }))
      .filter((r) => r.visitCount > 0)
      .sort((a, b) => b.visitCount - a.visitCount);
  }, [orders, users]);

  return (
    <div className={shared.pageContainerWithTabs}>
      <header className={shared.pageHeader}>
        <div className={shared.headerContainer}>
          <h1 className={shared.headerTitle}>BANNAI&apos;S BAR - 店舗状況</h1>
          <button type="button" onClick={() => navigate('/dashboard')} className={`${shared.backButton} ${styles.backButton}`}>
            戻る
          </button>
        </div>
      </header>

      <main className={shared.mainContentWithTabs}>
        <div className={styles.tabContainer}>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'status' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('status')}
          >
            現在の状況
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'ranking' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('ranking')}
          >
            ランキング
          </button>
        </div>

        <div className={shared.tabContentScroll}>
          {loading ? (
            <div className={localStyles.loading}>読み込み中...</div>
          ) : (
            <>
              {activeTab === 'status' && (
              <div className={localStyles.statusTab}>
                <section className={shared.card}>
                  <h2 className={shared.cardTitle}>バーに参加している人</h2>
                  {todayParticipantIds.length === 0 ? (
                    <p className={localStyles.emptyText}>本日はまだ参加者はいません。</p>
                  ) : (
                    <ul className={localStyles.participantIconList}>
                      {todayParticipantIds.map((uid) => (
                        <li key={uid} className={localStyles.participantIconItem}>
                          <button
                            type="button"
                            className={localStyles.participantIcon}
                            onClick={() => setParticipantDetailUserId(uid)}
                            title={displayName(uid)}
                            aria-label={`${displayName(uid)}の本日の注文を見る`}
                          >
                            <span className={localStyles.participantIconInitial}>{getInitial(uid)}</span>
                          </button>
                          <span className={localStyles.participantIconName}>{displayName(uid)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            )}

            {activeTab === 'ranking' && (
              <div className={localStyles.rankingTab}>
                <section className={shared.card}>
                  <h2 className={shared.cardTitle}>訪問回数ランキング</h2>
                  {rankingByUser.length === 0 ? (
                    <p className={localStyles.emptyText}>まだ訪問履歴がありません。</p>
                  ) : (
                    <ul className={localStyles.rankingList}>
                      {rankingByUser.slice(0, 10).map((item, index) => {
                        const rank = index + 1;
                        const rankClass = localStyles[`rank${rank}`] || '';
                        return (
                          <li
                            key={item.userId}
                            className={`${localStyles.rankingRow} ${rankClass}`}
                            onClick={() => setRankingDetailUserId(item.userId)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setRankingDetailUserId(item.userId);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <span className={localStyles.rankBadge}>{rank}</span>
                            <span className={localStyles.rankingNickname}>{displayName(item.userId)}</span>
                            <span className={localStyles.rankingCount}>{item.visitCount}回</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              </div>
            )}
          </>
          )}
        </div>
      </main>

      {rankingDetailUserId && (
        <div
          className={localStyles.modalOverlay}
          onClick={() => setRankingDetailUserId(null)}
          onKeyDown={(e) => e.key === 'Escape' && setRankingDetailUserId(null)}
          role="dialog"
          aria-modal="true"
          aria-label="注文詳細"
        >
          <div className={localStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={localStyles.modalTitle}>
              {displayName(rankingDetailUserId)} の注文詳細
            </h3>
            <ul className={localStyles.detailList}>
              {(rankingByUser.find((r) => r.userId === rankingDetailUserId)?.orders || []).map((o) => (
                <li key={o.id} className={localStyles.detailRow}>
                  <span className={localStyles.detailProduct}>{o.productName || '—'}</span>
                  <span className={localStyles.detailDate}>{formatDate(o.orderedAt)} {formatTime(o.orderedAt)}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className={localStyles.modalClose}
              onClick={() => setRankingDetailUserId(null)}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* 参加者クリック時：本日の注文履歴ポップアップ */}
      {participantDetailUserId && (
        <div
          className={localStyles.modalOverlay}
          onClick={() => setParticipantDetailUserId(null)}
          onKeyDown={(e) => e.key === 'Escape' && setParticipantDetailUserId(null)}
          role="dialog"
          aria-modal="true"
          aria-label="本日の注文履歴"
        >
          <div className={localStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={localStyles.modalTitle}>
              {displayName(participantDetailUserId)} の本日の注文
            </h3>
            <ul className={localStyles.detailList}>
              {todayOrders
                .filter((o) => o.userId === participantDetailUserId)
                .map((o) => (
                  <li key={o.id} className={localStyles.detailRow}>
                    <span className={localStyles.detailProduct}>{o.productName || '—'}</span>
                    <span className={localStyles.detailTime}>{formatTime(o.orderedAt)}</span>
                  </li>
                ))}
            </ul>
            <button
              type="button"
              className={localStyles.modalClose}
              onClick={() => setParticipantDetailUserId(null)}
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreStatusPage;
